<template>
  <div
    class="simple-rich-text"
    :class="{ 'show-toolbar': showToolbar }"
    :style="{ '--accent-color': accentColor || '#000000' }"
  >
    <div class="simple-rich-text-toolbar">
      <v-btn
        icon
        size="x-small"
        variant="text"
        class="simple-rich-text-button"
        title="Bold"
        @mousedown.prevent
        @click="toggleBold"
      >
        <v-icon size="18">mdi-format-bold</v-icon>
      </v-btn>
    </div>
    <div
      ref="editor"
      class="simple-rich-text-editor"
      contenteditable="true"
      :spellcheck="false"
      @focus="handleFocus"
      @blur="handleBlur"
      @input="handleInput"
      @paste.prevent="handlePaste"
    ></div>
  </div>
</template>

<script>
export default {
  name: 'SimpleRichTextBox',
  props: {
    modelValue: {
      type: String,
      default: '',
    },
    accentColor: {
      type: String,
      default: '#000000',
    },
  },
  emits: ['update:modelValue', 'change'],
  data() {
    return {
      isFocused: false,
      hasSelection: false,
    };
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(value) {
        this.$nextTick(() => {
          if (this.isFocused) return;
          this.setEditorHtml(value);
        });
      },
    },
  },
  computed: {
    showToolbar() {
      return this.isFocused && this.hasSelection;
    },
  },
  mounted() {
    this.setEditorHtml(this.modelValue);
    document.addEventListener('selectionchange', this.updateSelectionState);
  },
  beforeUnmount() {
    document.removeEventListener('selectionchange', this.updateSelectionState);
  },
  methods: {
    escapeHtml(value = '') {
      return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    },
    sanitizeHtml(value = '') {
      const template = document.createElement('template');
      template.innerHTML = String(value || '');
      const allowedInline = new Set(['STRONG', 'B', 'BR']);

      const cleanNode = (node) => {
        if (node.nodeType === Node.TEXT_NODE) {
          return document.createTextNode(node.textContent || '');
        }

        if (node.nodeType !== Node.ELEMENT_NODE) {
          return document.createTextNode('');
        }

        if (node.tagName === 'DIV' || node.tagName === 'P') {
          const fragment = document.createDocumentFragment();
          Array.from(node.childNodes).forEach((child) => {
            fragment.appendChild(cleanNode(child));
          });
          fragment.appendChild(document.createElement('br'));
          return fragment;
        }

        if (!allowedInline.has(node.tagName)) {
          const fragment = document.createDocumentFragment();
          Array.from(node.childNodes).forEach((child) => {
            fragment.appendChild(cleanNode(child));
          });
          return fragment;
        }

        const tagName = node.tagName === 'B' ? 'strong' : node.tagName.toLowerCase();
        const clean = document.createElement(tagName);
        Array.from(node.childNodes).forEach((child) => {
          clean.appendChild(cleanNode(child));
        });
        return clean;
      };

      const output = document.createElement('div');
      Array.from(template.content.childNodes).forEach((child) => {
        output.appendChild(cleanNode(child));
      });

      return output.innerHTML
        .replace(/(<br>\s*)+$/g, '')
        .trim();
    },
    toEditorHtml(value = '') {
      const text = String(value || '');
      if (/<\/?(strong|b|br|div|p)\b/i.test(text)) {
        return this.sanitizeHtml(text);
      }
      return this.escapeHtml(text).replace(/\r?\n/g, '<br>');
    },
    setEditorHtml(value = '') {
      const editor = this.$refs.editor;
      if (!editor) return;
      const nextHtml = this.toEditorHtml(value);
      if (editor.innerHTML !== nextHtml) {
        editor.innerHTML = nextHtml;
      }
    },
    getCleanValue() {
      const editor = this.$refs.editor;
      if (!editor) return '';
      return this.sanitizeHtml(editor.innerHTML);
    },
    emitCurrentValue() {
      const value = this.getCleanValue();
      this.$emit('update:modelValue', value);
      this.$emit('change', value);
    },
    handleInput() {
      this.emitCurrentValue();
      this.updateSelectionState();
    },
    handleBlur() {
      window.setTimeout(() => {
        this.isFocused = false;
        this.hasSelection = false;
        this.emitCurrentValue();
      }, 0);
    },
    handleFocus() {
      this.isFocused = true;
      this.updateSelectionState();
    },
    updateSelectionState() {
      const editor = this.$refs.editor;
      const selection = window.getSelection?.();
      if (!editor || !selection || selection.rangeCount === 0 || selection.isCollapsed) {
        this.hasSelection = false;
        return;
      }

      const range = selection.getRangeAt(0);
      const container = range.commonAncestorContainer;
      this.hasSelection = editor.contains(container.nodeType === Node.ELEMENT_NODE
        ? container
        : container.parentNode);
    },
    handlePaste(event) {
      const text = event.clipboardData?.getData('text/plain') || '';
      document.execCommand('insertText', false, text);
      this.emitCurrentValue();
    },
    toggleBold() {
      const editor = this.$refs.editor;
      if (!editor) return;
      editor.focus();
      document.execCommand('bold', false, null);
      this.emitCurrentValue();
    },
    focus() {
      this.$refs.editor?.focus();
    },
  },
};
</script>

<style scoped>
.simple-rich-text {
  position: relative;
  padding-top: 10px;
}

.simple-rich-text-toolbar {
  position: absolute;
  top: 12px;
  right: 6px;
  z-index: 2;
  display: flex;
  align-items: center;
  gap: 2px;
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 6px;
  opacity: 0;
  pointer-events: none;
  transition: opacity 120ms ease;
}

.simple-rich-text.show-toolbar .simple-rich-text-toolbar {
  opacity: 1;
  pointer-events: auto;
}

.simple-rich-text-button {
  color: rgba(0, 0, 0, 0.72);
}

.simple-rich-text-editor {
  width: 100%;
  min-height: 40px;
  padding: 9px 46px 8px 12px;
  border: 1px solid rgba(0, 0, 0, 0.38);
  border-radius: 4px;
  background: #ffffff;
  color: rgba(0, 0, 0, 0.87);
  font-size: 16px;
  line-height: 1.4;
  outline: none;
  white-space: pre-wrap;
  word-break: break-word;
}

.simple-rich-text-editor:focus {
  border-color: var(--accent-color);
  border-width: 2px;
  padding: 8px 45px 7px 11px;
}
</style>
